import { useState, useRef, useCallback, useEffect } from 'react';
import {
  checkAudioFormatSupport,
  getOptimalRecorderConfig,
  getOptimalAudioConstraints,
  validateAudioChunk,
  formatAudioError
} from '../lib/audioUtils';
import { errorHandler, performanceMonitor } from '../lib/errorHandler';

export interface AudioChunk {
  data: Blob;
  timestamp: number;
  sequenceId: number;
}

export interface AudioCaptureError {
  type: 'PERMISSION_DENIED' | 'FORMAT_UNSUPPORTED' | 'DEVICE_ERROR' | 'RECORDING_ERROR';
  message: string;
}

export interface AudioCaptureState {
  isRecording: boolean;
  isSupported: boolean;
  error: AudioCaptureError | null;
  stream: MediaStream | null;
}

export interface UseAudioCaptureOptions {
  chunkInterval?: number; // in milliseconds, default 2000ms (2 seconds for larger chunks)
  onAudioChunk?: (chunk: AudioChunk) => void;
  onError?: (error: AudioCaptureError) => void;
}

export const useAudioCapture = (options: UseAudioCaptureOptions = {}) => {
  const {
    chunkInterval = 2000, // Increased to 2 seconds for larger chunks
    onAudioChunk,
    onError
  } = options;

  const [state, setState] = useState<AudioCaptureState>({
    isRecording: false,
    isSupported: false,
    error: null,
    stream: null
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sequenceIdRef = useRef<number>(0);
  const chunksRef = useRef<Blob[]>([]);

  // Check if WebM/Opus is supported
  const checkSupport = useCallback(() => {
    console.log('useAudioCapture: Checking support...');

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('navigator.mediaDevices or getUserMedia not available');
      return false;
    }

    const support = checkAudioFormatSupport();
    const isSupported = support.preferredMimeType !== null;
    console.log('useAudioCapture: Support check result:', isSupported);
    return isSupported;
  }, []);

  // Initialize support check on mount
  useEffect(() => {
    const isSupported = checkSupport();
    setState(prev => ({ ...prev, isSupported }));

    if (!isSupported) {
      const error: AudioCaptureError = {
        type: 'FORMAT_UNSUPPORTED',
        message: 'WebM/Opus format is not supported in this browser'
      };
      setState(prev => ({ ...prev, error }));
      // Don't call onError for format support issues during initialization
      // The UI will handle this by showing the disabled state

      // Log to error handler for debugging purposes only
      console.warn('WebM/Opus format not supported in this browser');
    }
  }, [checkSupport]);

  const handleError = useCallback((error: AudioCaptureError) => {
    setState(prev => ({ ...prev, error, isRecording: false }));
    onError?.(error);

    // Log to error handler with appropriate type
    const errorType = error.type === 'PERMISSION_DENIED' ? 'PERMISSION_ERROR' :
                     error.type === 'FORMAT_UNSUPPORTED' ? 'FORMAT_ERROR' :
                     'AUDIO_ERROR';

    errorHandler.handleError(
      new Error(error.message),
      errorType,
      {
        component: 'useAudioCapture',
        operation: 'audio_capture',
        metadata: { errorType: error.type }
      }
    );
  }, [onError]);

  const startRecording = useCallback(async () => {
    const endTimer = performanceMonitor.startTimer('audio_capture_start');

    if (!state.isSupported) {
      handleError({
        type: 'FORMAT_UNSUPPORTED',
        message: 'WebM/Opus format is not supported'
      });
      endTimer();
      return;
    }

    try {
      // Request microphone access with optimal constraints
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: getOptimalAudioConstraints()
      });

      console.log('stream', stream)

      streamRef.current = stream;

      // Get optimal recorder configuration
      const recorderConfig = getOptimalRecorderConfig();

      // Create MediaRecorder with WebM/Opus configuration
      const mediaRecorder = new MediaRecorder(stream, recorderConfig);

      console.log('mediaRecorder', mediaRecorder)

      mediaRecorderRef.current = mediaRecorder;
      sequenceIdRef.current = 0;
      chunksRef.current = [];

      // Handle data available event
      mediaRecorder.ondataavailable = (event) => {
        console.log('Audio chunk received:', {
          size: event.data.size,
          type: event.data.type,
          sequenceId: sequenceIdRef.current
        });
        
        if (event.data && event.data.size > 0) {
          // Validate the audio chunk before processing
          if (validateAudioChunk(event.data)) {
            const chunk: AudioChunk = {
              data: event.data,
              timestamp: Date.now(),
              sequenceId: sequenceIdRef.current++
            };

            console.log('Valid audio chunk created:', {
              size: chunk.data.size,
              sequenceId: chunk.sequenceId,
              timestamp: chunk.timestamp
            });

            onAudioChunk?.(chunk);
          } else {
            console.warn('Invalid audio chunk received, skipping:', {
              size: event.data.size,
              type: event.data.type
            });
          }
        } else {
          console.warn('Empty audio chunk received');
        }
      };

      // Handle recording errors
      mediaRecorder.onerror = (event) => {
        const errorEvent = event as any; // MediaRecorderErrorEvent
        const error: AudioCaptureError = {
          type: 'RECORDING_ERROR',
          message: `Recording error: ${errorEvent.error?.message || 'Unknown recording error'}`
        };
        handleError(error);
      };

      // Start recording with specified interval
      mediaRecorder.start(chunkInterval);

      setState(prev => ({
        ...prev,
        isRecording: true,
        error: null,
        stream
      }));

      const duration = endTimer();
      console.log(`Audio capture started in ${duration.toFixed(2)}ms`);

    } catch (err) {
      endTimer();
      let error: AudioCaptureError;

      if (err instanceof Error) {
        const errorMessage = formatAudioError(err);

        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          error = {
            type: 'PERMISSION_DENIED',
            message: errorMessage
          };
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          error = {
            type: 'DEVICE_ERROR',
            message: errorMessage
          };
        } else if (err.message.includes('supported') || err.message.includes('format')) {
          error = {
            type: 'FORMAT_UNSUPPORTED',
            message: errorMessage
          };
        } else {
          error = {
            type: 'DEVICE_ERROR',
            message: errorMessage
          };
        }
      } else {
        error = {
          type: 'DEVICE_ERROR',
          message: 'Unknown error occurred while accessing microphone'
        };
      }

      handleError(error);
    }
  }, [state.isSupported, chunkInterval, onAudioChunk, handleError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setState(prev => ({
      ...prev,
      isRecording: false,
      stream: null
    }));
  }, [state.isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  return {
    ...state,
    startRecording,
    stopRecording
  };
};
